import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, Calendar, ChevronDown, ChevronLeft, ChevronRight, Loader2, RefreshCw, Radio, Timer, Play, Pause, Square, UsersRound, Download, UserCheck, AlertTriangle, Receipt } from '../ui/icons';
import Card from '../ui/Card';
import AttributionBadge, { ATTRIBUTION_APP, attributionLabel, LocationTag, USER_CARD, UserCardFooter, useAttributions, useUserLocations } from '../ui/AttributionBadge';
import Button from '../ui/Button';
import { exportToCsv } from '../../utils/csv';
import Colors, { withAlpha } from '../../constants/Colors';
import { gamesApi, FirebaseUserDailyAudioRow, firebaseAnalyticsApi, UserGameEventDetail, StatsRow, AudioEvent, ListeningDurationEvent, CurrentlyListeningUserRow, FreeTrialLogRow, THEME_MODE } from '../../services/api';

const PAGE_SIZE = 10;

// Session classification, from fields the app writes on the RTDB session node:
// - purchase: `event` is one of PURCHASE_EVENTS and isFreeTrial is false (live or ended);
// - sign-up:  ended with reason identity_changed (guest -> account);
// - drop:     any other ended session.
// Edit these maps if the app's values change.
const SIGNUP_REASONS = ['identity_changed'];
// Drops caused by the user signing in / up mid-session: stay in Dropped, but the card
// highlights the account email the session switched to.
const IDENTITY_SWITCH_REASONS: Record<string, string> = {
  identity_changed_sign_in: 'Signed in as',
  identity_changed_sign_up: 'Signed up as',
};
const PURCHASE_EVENTS: Record<string, { label: string; color: string; bg: string }> = {
  converted_single_game: { label: 'Single Game', color: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)' },
  converted_football_season: { label: 'Football Season', color: '#15803d', bg: 'rgba(22, 163, 74, 0.1)' },
  converted_basketball_season: { label: 'Basketball Season', color: '#c2410c', bg: 'rgba(234, 88, 12, 0.1)' },
  converted_all_sport: { label: 'All Sports', color: '#7e22ce', bg: 'rgba(147, 51, 234, 0.1)' },
};
type SessionOutcome = 'drop' | 'signup' | 'purchase';
const normReason = (user: CurrentlyListeningUserRow) => (user.reason || '').trim().toLowerCase();
const normEvent = (user: CurrentlyListeningUserRow) => (user.event || '').trim().toLowerCase();
const isPurchase = (user: CurrentlyListeningUserRow) => !!PURCHASE_EVENTS[normEvent(user)] && !user.is_free_trial;
const sessionOutcome = (user: CurrentlyListeningUserRow): SessionOutcome => {
  if (isPurchase(user)) return 'purchase';
  if (SIGNUP_REASONS.includes(normReason(user))) return 'signup';
  return 'drop';
};
// Free-trial identity from the `user` field: an email = signed in, anything else (a UUID) = anonymous.
const isSignedInUser = (user: CurrentlyListeningUserRow) => /@/.test(user.user_id || '');

type PlatformFilter = 'all' | 'ios' | 'android';
type PlanFilter = 'all' | 'paid' | 'trial';

const ALL_GAMES_TAB = '__all__';
const UNKNOWN_GAME_LABEL = 'Unknown game';

function toDateInputValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDuration(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
}

// Compact "how long ago" for the live freshness indicator: 4s, 2m 5s, 1h 3m.
function formatAgo(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds));
  if (safe < 60) return `${safe}s`;
  if (safe < 3600) return `${Math.floor(safe / 60)}m ${safe % 60}s`;
  return `${Math.floor(safe / 3600)}h ${Math.floor((safe % 3600) / 60)}m`;
}

function formatClockTime(ms?: number | null): string {
  return ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
}

// Colour-coded count tiles in one row (same look as the Paywall Counter's per-user tiles);
// zero/empty values stay neutral so the active ones stand out.
type MetricTile = { label: string; value: number; display?: string; color: string };
function MetricTiles({ metrics }: { metrics: MetricTile[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))`, gap: '6px' }}>
      {metrics.map((m) => {
        const active = m.value > 0;
        const shown = m.display ?? m.value.toLocaleString();
        return (
          <div
            key={m.label}
            title={`${m.label}: ${shown}`}
            style={{
              minWidth: 0, padding: '6px 6px', borderRadius: '8px', textAlign: 'center',
              border: `1px solid ${active ? withAlpha(m.color, 0.28) : '#D8E2F7'}`,
              background: active ? withAlpha(m.color, 0.08) : 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <div style={{ fontSize: '9px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.02em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {m.label}
            </div>
            <div style={{ marginTop: '2px', fontSize: '16px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: active ? m.color : '#7A8BB5' }}>
              {shown}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Compact card used by Per-Game Breakdown and Drop Reasons: bold title line, small meta
// line, then a row of MetricTiles.
function MetricCard({ title, meta, badge, metrics, titleColor }: { title: string; meta?: string; badge?: React.ReactNode; metrics: MetricTile[]; titleColor?: string }) {
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0,
        padding: '12px 14px', borderRadius: '12px',
        border: '1px solid var(--border-color, #D8E2F7)', background: '#FFFFFF',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
          <div
            title={title}
            style={{ minWidth: 0, fontWeight: 700, fontSize: '13px', fontFamily: 'monospace, sans-serif', color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title}
          </div>
          {badge}
        </div>
        {meta && <div style={{ marginTop: '2px', fontSize: '10px', color: 'var(--text-secondary, #64748b)' }}>{meta}</div>}
      </div>
      <MetricTiles metrics={metrics} />
    </div>
  );
}

const METRIC_CARD_GRID: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))', gap: '10px' };

type GameGroupable = { game_name?: string | null; game_id?: string | null };

// Tabs ("All" + one per game, with counts) over a list of listener cards. "All" shows
// every game as its own headed section; a game tab shows just that game's cards.
function GameGroupedCards<T extends GameGroupable>({
  users,
  renderCard,
  accent,
  accentBg,
  countLabel,
  showTabs = true,
}: {
  users: T[];
  renderCard: (user: T, idx: number) => React.ReactNode;
  accent: string;
  accentBg: string;
  countLabel: string;
  /** false = plain card grid (used inside a single game's Real Time tab). */
  showTabs?: boolean;
}) {
  const [selectedTab, setSelectedTab] = useState<string>(ALL_GAMES_TAB);

  // Most users first; each group keeps the incoming order.
  const groups = useMemo(() => {
    const byGame = new Map<string, T[]>();
    users.forEach((user) => {
      const game = user.game_name || user.game_id || UNKNOWN_GAME_LABEL;
      byGame.set(game, [...(byGame.get(game) || []), user]);
    });
    return Array.from(byGame, ([game, items]) => ({ game, users: items }))
      .sort((a, b) => b.users.length - a.users.length || a.game.localeCompare(b.game));
  }, [users]);

  // Fall back to "All" if the selected game drops out of the list after a refresh.
  const activeTab = groups.some((g) => g.game === selectedTab) ? selectedTab : ALL_GAMES_TAB;

  if (!showTabs) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
        {users.map((user, idx) => renderCard(user, idx))}
      </div>
    );
  }
  const tabs = [{ key: ALL_GAMES_TAB, label: 'All', count: users.length },
    ...groups.map((g) => ({ key: g.game, label: g.game, count: g.users.length }))];

  return (
    <>
      <div className="teams-editor-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`teams-editor-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setSelectedTab(tab.key)}
            title={tab.label}
            style={{ maxWidth: '280px' }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tab.label}</span>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px', background: accentBg, color: accent, flexShrink: 0 }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {(activeTab === ALL_GAMES_TAB ? groups : groups.filter((g) => g.game === activeTab)).map((group) => (
        <div key={group.game} style={{ marginTop: '12px' }}>
          {activeTab === ALL_GAMES_TAB && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
              <span style={{ fontFamily: 'monospace, sans-serif' }}>{group.game}</span>
              <span style={{ fontSize: '11px', fontWeight: 600, color: accent }}>{group.users.length} {countLabel}</span>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
            {group.users.map((user, idx) => renderCard(user, idx))}
          </div>
        </div>
      ))}
    </>
  );
}

function getInitials(userId: string): string {
  if (!userId) return 'US';
  const name = userId.split('@')[0].replace(/[^a-zA-Z]/g, '');
  if (name.length >= 2) {
    return name.slice(0, 2).toUpperCase();
  }
  return (userId.slice(0, 2) || 'US').toUpperCase();
}

function parseDateInputValue(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

type ThemedDatePickerProps = {
  label: string;
  value: string;
  max?: string;
  onChange: (value: string) => void;
};

function ThemedDatePicker({ label, value, max, onChange }: ThemedDatePickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseDateInputValue(value);
  const maxDate = max ? parseDateInputValue(max) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) setViewMonth(selectedDate);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(monthStart.getDate() - monthStart.getDay());

  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(calendarStart);
    day.setDate(calendarStart.getDate() + index);
    return day;
  });

  const monthLabel = monthStart.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="analytics-filter analytics-date-filter" ref={containerRef}>
      <span className="analytics-filter-label">{label}</span>
      <button
        type="button"
        className="analytics-date-trigger"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={`Select ${label.toLowerCase()} date`}
      >
        <span>{value}</span>
        <Calendar size={14} />
      </button>

      {isOpen && (
        <div className="analytics-date-popover">
          <div className="analytics-date-header">
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="analytics-date-title">{monthLabel}</div>
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="analytics-date-grid analytics-date-grid-head">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>

          <div className="analytics-date-grid">
            {days.map((day) => {
              const inMonth = day >= monthStart && day <= monthEnd;
              const isSelected =
                !!selectedDate &&
                day.getFullYear() === selectedDate.getFullYear() &&
                day.getMonth() === selectedDate.getMonth() &&
                day.getDate() === selectedDate.getDate();
              const isDisabled = !!maxDate && day > maxDate;

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`analytics-date-day ${inMonth ? '' : 'muted'} ${isSelected ? 'selected' : ''}`}
                  disabled={isDisabled}
                  onClick={() => {
                    onChange(toDateInputValue(day));
                    setIsOpen(false);
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ET wall-clock for an ISO timestamp, e.g. "10/17, 7:04 PM".
function formatEt(iso?: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "Free Trial Past Logs": free-trial and converted users for a chosen Eastern-time day, from
 * the GA `free_trial` / `converted_user` events in BigQuery (available from 2026-10-16). */
function FreeTrialPastLogs({ appEnv, todayValue }: { appEnv: string; todayValue: string }) {
  const yesterday = (() => {
    const d = parseDateInputValue(todayValue) || new Date();
    d.setDate(d.getDate() - 1);
    return toDateInputValue(d);
  })();
  const [day, setDay] = useState(yesterday);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeTrial, setFreeTrial] = useState<FreeTrialLogRow[]>([]);
  const [converted, setConverted] = useState<FreeTrialLogRow[]>([]);
  const requestId = useRef(0);

  useEffect(() => {
    if (!day) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    firebaseAnalyticsApi.getFreeTrialLogs(day, appEnv).then((res) => {
      if (id !== requestId.current) return; // a newer date was picked meanwhile
      if (res.success) {
        setFreeTrial(res.free_trial);
        setConverted(res.converted);
      } else {
        setFreeTrial([]);
        setConverted([]);
        setError(res.error || 'Failed to load free trial logs');
      }
      setLoading(false);
    });
  }, [day, appEnv]);

  const attributionFor = useAttributions([...freeTrial, ...converted].map((row) => row.user), appEnv);
  const locationFor = useUserLocations([...freeTrial, ...converted].map((row) => row.user), appEnv);
  const signedIn = freeTrial.filter((row) => /@/.test(row.user)).length;
  const byType = Object.entries(PURCHASE_EVENTS).map(([event, meta]) => ({
    ...meta,
    count: converted.filter((row) => (row.conversion_event || '').toLowerCase() === event).length,
  }));

  const handleExport = () => {
    const rows = [
      ...freeTrial.map((row) => ({ ...row, kind: 'Free trial' })),
      ...converted.map((row) => ({ ...row, kind: 'Converted' })),
    ];
    exportToCsv(`free-trial-logs-${day}.csv`, rows, [
      { header: 'Type', value: (r) => r.kind },
      { header: 'User', value: (r) => r.user },
      { header: 'Account', value: (r) => (/@/.test(r.user) ? 'Signed in' : 'Anonymous') },
      { header: 'Install source', value: (r) => { const a = attributionFor(r.user); return a ? attributionLabel(a) : ''; } },
      { header: 'Location', value: (r) => locationFor(r.user) || '' },
      { header: 'Games', value: (r) => (r.games || []).join('; ') },
      { header: 'Device', value: (r) => r.device || '' },
      { header: 'Conversion', value: (r) => (r.conversion_events && r.conversion_events.length ? r.conversion_events : [r.conversion_event]).filter(Boolean).join('; ') },
      { header: 'Last reason', value: (r) => r.last_reason || '' },
      { header: 'First seen (ET)', value: (r) => formatEt(r.first_seen) },
      { header: 'Last seen (ET)', value: (r) => formatEt(r.last_seen) },
      { header: 'Events', value: (r) => r.events },
    ]);
  };

  const renderRow = (accent: string) => (row: FreeTrialLogRow, idx: number) => {
    const isSignedIn = /@/.test(row.user);
    const product = PURCHASE_EVENTS[(row.conversion_event || '').toLowerCase()];
    return (
      <div
        key={`${row.user}-${idx}`}
        style={{
          display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0,
          padding: '14px 16px', borderRadius: '12px',
          background: USER_CARD.background,
          border: `1px solid ${USER_CARD.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <span style={{
            width: '28px', height: '28px', borderRadius: '999px', flex: 'none', display: 'grid', placeItems: 'center',
            fontSize: '11px', fontWeight: 700, background: withAlpha(Colors.secondary, 0.16), color: Colors.secondary,
          }}
          >
            {row.user.slice(0, 2).toUpperCase()}
          </span>
          <span title={row.user} style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '13px', fontFamily: 'monospace, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.user}
          </span>
          <span style={{ flexShrink: 0, fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', color: isSignedIn ? '#1d4ed8' : '#475569', background: isSignedIn ? 'rgba(37, 99, 235, 0.1)' : 'rgba(100, 116, 139, 0.12)' }}>
            {isSignedIn ? 'Signed in' : 'Anonymous'}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          {product && (
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '12px', textTransform: 'uppercase', color: product.color, background: product.bg }}>{product.label}</span>
          )}
          {row.device && (
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '12px', background: 'rgba(147, 51, 234, 0.1)', color: '#7e22ce', textTransform: 'uppercase' }}>📱 {row.device}</span>
          )}
          {row.last_reason && (
            <span style={{ fontSize: '11px', fontFamily: 'monospace, sans-serif', color: accent }}>reason: {row.last_reason}</span>
          )}
        </div>
        {(row.games || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {row.games.map((game) => (
              <span
                key={game}
                title={game}
                style={{ fontSize: '11px', fontWeight: 500, padding: '3px 9px', borderRadius: '12px', background: 'var(--badge-bg, #e2e8f0)', color: 'var(--text-secondary, #475569)', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {game}
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: '11.5px', color: 'var(--text-secondary, #64748b)' }}>
          {formatEt(row.first_seen)} → {formatEt(row.last_seen)} ET · {row.events} event{row.events === 1 ? '' : 's'}
        </div>
        <UserCardFooter location={locationFor(row.user)} attribution={attributionFor(row.user)} />
      </div>
    );
  };

  return (
    <Card
      title="Free Trial Past Logs"
      description="Free-trial and converted users for the selected day (Eastern time), from analytics events. Available from Oct 16, 2026."
      headerAction={
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          <ThemedDatePicker label="Date" value={day} max={todayValue} onChange={setDay} />
          <Button variant="outline" onClick={handleExport} disabled={loading || (freeTrial.length === 0 && converted.length === 0)}>
            <Download size={14} />
            Export CSV
          </Button>
        </div>
      }
    >
      {error && <div className="alert alert-danger" style={{ marginBottom: '12px' }}>{error}</div>}
      {loading ? (
        <div className="analytics-summary-meta" style={{ padding: '24px 0', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Loader2 size={16} className="spin" /> Loading {day}…
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '16px' }}>
            <MetricTiles
              metrics={[
                { label: 'Free trial users', value: freeTrial.length, color: '#b45309' },
                { label: 'Anonymous', value: freeTrial.length - signedIn, color: '#475569' },
                { label: 'Signed in', value: signedIn, color: '#1d4ed8' },
                { label: 'Converted', value: converted.length, color: '#7e22ce' },
                ...byType.map((t) => ({ label: t.label, value: t.count, color: t.color })),
              ]}
            />
          </div>

          <h4 className="analytics-past-heading">Free Trial Users <span>{freeTrial.length}</span></h4>
          {freeTrial.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '8px 0 16px' }}>No free-trial users on {day}.</div>
          ) : (
            // One card per user (a user can span several games), latest activity first.
            <GameGroupedCards users={freeTrial} renderCard={renderRow('#b45309')} accent="#b45309" accentBg="rgba(245, 158, 11, 0.12)" countLabel="on free trial" showTabs={false} />
          )}

          <h4 className="analytics-past-heading" style={{ marginTop: '20px' }}>Converted Users <span>{converted.length}</span></h4>
          {converted.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '8px 0' }}>No conversions on {day}.</div>
          ) : (
            <GameGroupedCards users={converted} renderCard={renderRow('#7e22ce')} accent="#7e22ce" accentBg="rgba(147, 51, 234, 0.1)" countLabel="converted" showTabs={false} />
          )}
        </>
      )}
    </Card>
  );
}

export default function AnalyticsPanel() {
  const [rows, setRows] = useState<FirebaseUserDailyAudioRow[]>([]);
  const [stats, setStats] = useState<StatsRow | null>(null);
  const [currentlyListeningUsers, setCurrentlyListeningUsers] = useState<CurrentlyListeningUserRow[]>([]);
  const [droppedListeners, setDroppedListeners] = useState<CurrentlyListeningUserRow[]>([]);
  // ===== Real Time Logs: filter bar (shared by every real-time block below) =====
  const [rtSearch, setRtSearch] = useState('');
  const [rtPlatform, setRtPlatform] = useState<PlatformFilter>('all');
  const [rtPlan, setRtPlan] = useState<PlanFilter>('all');
  const filteredLiveUsers = useMemo(() => {
    const query = rtSearch.trim().toLowerCase();
    return currentlyListeningUsers.filter((user) =>
      (!query || (user.user_id || '').toLowerCase().includes(query) || (user.email || '').toLowerCase().includes(query))
      && (rtPlatform === 'all' || (user.device_type || '').toLowerCase() === rtPlatform)
      && (rtPlan === 'all' || (rtPlan === 'trial' ? !!user.is_free_trial : !user.is_free_trial)));
  }, [currentlyListeningUsers, rtSearch, rtPlatform, rtPlan]);
  const filteredDroppedUsers = useMemo(() => {
    const query = rtSearch.trim().toLowerCase();
    return droppedListeners.filter((user) =>
      (!query || (user.user_id || '').toLowerCase().includes(query) || (user.email || '').toLowerCase().includes(query))
      && (rtPlatform === 'all' || (user.device_type || '').toLowerCase() === rtPlatform)
      && (rtPlan === 'all' || (rtPlan === 'trial' ? !!user.is_free_trial : !user.is_free_trial)));
  }, [droppedListeners, rtSearch, rtPlatform, rtPlan]);
  const rtFiltersActive = !!rtSearch.trim() || rtPlatform !== 'all' || rtPlan !== 'all';

  // Real Time Logs sub-views: live sessions (today, RTDB) vs free-trial past logs (BigQuery).
  const [rtView, setRtView] = useState<'live' | 'free-trial-past'>('live');

  // ===== Real Time Logs: top-level game tabs =====
  // Tabs = today's scheduled games (games API, Eastern-time "today") + any game with a
  // session today. Everything inside the Real Time section is scoped to the selected tab;
  // "All games" keeps the cross-game overview. Default = busiest game (most live).
  const [todaysScheduledGames, setTodaysScheduledGames] = useState<string[]>([]);
  const [rtGameTab, setRtGameTab] = useState<string | null>(null); // null = auto (busiest)
  // Install source (organic / social) and location for every user shown in the live, dropped and historic views.
  const lookupIds = [...currentlyListeningUsers, ...droppedListeners].flatMap((u) => [u.user_id, u.email]).concat(rows.map((r) => r.user_id));
  const attributionFor = useAttributions(lookupIds, ATTRIBUTION_APP);
  const locationFor = useUserLocations(lookupIds, ATTRIBUTION_APP);
  const userAttribution = (user: CurrentlyListeningUserRow) => attributionFor(user.user_id) || attributionFor(user.email);
  const userLocation = (user: CurrentlyListeningUserRow) => locationFor(user.user_id) || locationFor(user.email);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await gamesApi.listGames({ status: 'scheduled', bucket: 'today', page: 1, page_size: 200 });
      if (!cancelled && response.success && response.data) {
        setTodaysScheduledGames((response.data.games || []).map((g) => g.game_name).filter(Boolean) as string[]);
      }
    };
    load();
    const id = window.setInterval(load, 5 * 60 * 1000); // pick up games published later today
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);
  const sessionGameKey = (user: CurrentlyListeningUserRow) => user.game_name || user.game_id || UNKNOWN_GAME_LABEL;
  const rtGameTabs = useMemo(() => {
    const stats = new Map<string, { live: number; ended: number }>();
    const bump = (game: string, field: 'live' | 'ended') => {
      const row = stats.get(game) || { live: 0, ended: 0 };
      row[field] += 1;
      stats.set(game, row);
    };
    todaysScheduledGames.forEach((game) => { if (!stats.has(game)) stats.set(game, { live: 0, ended: 0 }); });
    filteredLiveUsers.forEach((user) => bump(sessionGameKey(user), 'live'));
    filteredDroppedUsers.forEach((user) => bump(sessionGameKey(user), 'ended'));
    return Array.from(stats, ([game, counts]) => ({ game, ...counts }))
      .sort((a, b) => b.live - a.live || b.ended - a.ended || a.game.localeCompare(b.game));
  }, [todaysScheduledGames, filteredLiveUsers, filteredDroppedUsers]);
  const activeGameTab = rtGameTab && (rtGameTab === ALL_GAMES_TAB || rtGameTabs.some((t) => t.game === rtGameTab))
    ? rtGameTab
    : rtGameTabs[0]?.game ?? ALL_GAMES_TAB;
  const inActiveGame = (user: CurrentlyListeningUserRow) => activeGameTab === ALL_GAMES_TAB || sessionGameKey(user) === activeGameTab;
  const liveInTab = useMemo(() => filteredLiveUsers.filter(inActiveGame), [filteredLiveUsers, activeGameTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const droppedInTab = useMemo(() => filteredDroppedUsers.filter(inActiveGame), [filteredDroppedUsers, activeGameTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live listeners split by plan: paid go to "Currently Listening", free trial to
  // "Free Trial Listeners", so each user shows in exactly one card.
  const paidListeners = useMemo(
    () => liveInTab.filter((user) => !user.is_free_trial),
    [liveInTab]
  );
  const freeTrialListeners = useMemo(
    () => liveInTab.filter((user) => user.is_free_trial),
    [liveInTab]
  );

  // Ended sessions split by outcome: real drops, sign-ups, purchases (see sessionOutcome).
  const droppedOnlyUsers = useMemo(
    () => droppedInTab.filter((user) => sessionOutcome(user) === 'drop'),
    [droppedInTab]
  );
  // Purchases count whether the session is still live or has ended (buyers usually keep listening).
  const purchasedUsers = useMemo(
    () => [...liveInTab, ...droppedInTab].filter(isPurchase),
    [liveInTab, droppedInTab]
  );
  // Cross-game purchases, for the Per-Game Breakdown overview (not tab-scoped).
  const allPurchasedUsers = useMemo(
    () => [...filteredLiveUsers, ...filteredDroppedUsers].filter(isPurchase),
    [filteredLiveUsers, filteredDroppedUsers]
  );
  const purchasedByType = useMemo(
    () => Object.entries(PURCHASE_EVENTS).map(([event, meta]) => ({
      reason: event,
      ...meta,
      count: purchasedUsers.filter((user) => normEvent(user) === event).length,
    })),
    [purchasedUsers]
  );
  // Free-trial listeners split by identity (see isSignedInUser): `user` = UUID vs email.
  const anonymousTrialUsers = useMemo(() => freeTrialListeners.filter((user) => !isSignedInUser(user)), [freeTrialListeners]);
  const signedInTrialUsers = useMemo(() => freeTrialListeners.filter(isSignedInUser), [freeTrialListeners]);

  // ===== Real Time Logs: KPI strip (all games - it sits above the game tabs) =====
  const allDroppedOnlyUsers = useMemo(
    () => filteredDroppedUsers.filter((user) => sessionOutcome(user) === 'drop'),
    [filteredDroppedUsers]
  );
  const rtKpis = useMemo(() => ({
    live: filteredLiveUsers.length,
    paid: filteredLiveUsers.filter((user) => !user.is_free_trial).length,
    trial: filteredLiveUsers.filter((user) => user.is_free_trial).length,
    dropped: allDroppedOnlyUsers.length,
    signedUp: filteredDroppedUsers.filter((user) => sessionOutcome(user) === 'signup').length,
    purchased: allPurchasedUsers.length,
  }), [filteredLiveUsers, filteredDroppedUsers, allDroppedOnlyUsers, allPurchasedUsers]);
  const allPurchasedByType = useMemo(
    () => Object.entries(PURCHASE_EVENTS).map(([event, meta]) => ({
      ...meta,
      count: allPurchasedUsers.filter((user) => normEvent(user) === event).length,
    })),
    [allPurchasedUsers]
  );

  // ===== Real Time Logs: per-game breakdown =====
  const rtGameBreakdown = useMemo(() => {
    const byGame = new Map<string, { game: string; live: number; paid: number; trial: number; dropped: number; signedUp: number; purchased: number }>();
    const entry = (user: CurrentlyListeningUserRow) => {
      const game = user.game_name || user.game_id || UNKNOWN_GAME_LABEL;
      if (!byGame.has(game)) byGame.set(game, { game, live: 0, paid: 0, trial: 0, dropped: 0, signedUp: 0, purchased: 0 });
      return byGame.get(game)!;
    };
    filteredLiveUsers.forEach((user) => {
      const row = entry(user);
      row.live += 1;
      if (user.is_free_trial) row.trial += 1; else row.paid += 1;
    });
    filteredDroppedUsers.forEach((user) => {
      const outcome = sessionOutcome(user);
      if (outcome === 'purchase') return; // counted below with live purchases
      const row = entry(user);
      if (outcome === 'signup') row.signedUp += 1;
      else row.dropped += 1;
    });
    allPurchasedUsers.forEach((user) => {
      entry(user).purchased += 1;
    });
    return Array.from(byGame.values())
      .sort((a, b) => b.live - a.live || b.dropped - a.dropped || a.game.localeCompare(b.game));
  }, [filteredLiveUsers, filteredDroppedUsers, allPurchasedUsers]);

  // Per-game user cards are accordions too, collapsed by default (header still shows the count).
  type UserCardKey = 'paid' | 'trial' | 'dropped' | 'purchased';
  const [userCardsOpen, setUserCardsOpen] = useState<Record<UserCardKey, boolean>>({
    paid: false, trial: false, dropped: false, purchased: false,
  });
  const userCardToggle = (key: UserCardKey) => ({
    collapsible: true,
    open: userCardsOpen[key],
    onToggle: () => setUserCardsOpen((prev) => ({ ...prev, [key]: !prev[key] })),
  });

  // Per-Game Breakdown / Drop Reasons are accordions, closed by default.
  const [perGameOpen, setPerGameOpen] = useState(false);
  const [dropReasonsOpen, setDropReasonsOpen] = useState(false);

  // ===== Real Time Logs: drop reasons summary =====
  const rtDropReasons = useMemo(() => {
    const counts = new Map<string, number>();
    allDroppedOnlyUsers.forEach((user) => {
      const reason = (user.reason || '').trim() || 'unknown';
      counts.set(reason, (counts.get(reason) || 0) + 1);
    });
    return Array.from(counts, ([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count);
  }, [allDroppedOnlyUsers]);

  // ===== Real Time Logs: freshness + pause =====
  const [liveUpdatedAtMs, setLiveUpdatedAtMs] = useState<number | null>(null);
  const [liveFetchFailed, setLiveFetchFailed] = useState(false);
  const [livePaused, setLivePaused] = useState(false);
  const [expandedEventDetails, setExpandedEventDetails] = useState<{ [key: string]: UserGameEventDetail }>({});
  const [loading, setLoading] = useState(false);
  const [rowLoadingKey, setRowLoadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [liveClockSeconds, setLiveClockSeconds] = useState(() => Math.floor(Date.now() / 1000));
  const [exporting, setExporting] = useState(false);

  const todayValue = toDateInputValue(new Date());

  const [dateFrom, setDateFrom] = useState(todayValue);
  const [dateTo, setDateTo] = useState(todayValue);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [gameNameFilter, setGameNameFilter] = useState('');

  const appEnv = THEME_MODE === 'dev' ? 'nonprod' : 'prod';


  const fetchMetrics = async () => {
    if (!dateFrom || !dateTo) {
      setError('Please select both From and To dates.');
      return;
    }

    const offset = (currentPage - 1) * PAGE_SIZE;
    const userFilter = userIdFilter.trim();
    const gameFilter = gameNameFilter.trim();

    setLoading(true);
    setError(null);

    try {
        // Fetch from backend APIs
        const [dailyDetailsResponse, statsResponse, listeningResponse] = await Promise.all([
          firebaseAnalyticsApi.getDailyDetails({
            startDate: dateFrom,
            endDate: dateTo,
            app: appEnv,
            limit: PAGE_SIZE,
            page: currentPage,
            offset,
            userId: userFilter || undefined,
            gameName: gameFilter || undefined,
          }),
          firebaseAnalyticsApi.getStats({
            startDate: dateFrom,
            endDate: dateTo,
            app: appEnv,
            liveWindowMinutes: 1,
            userId: userFilter || undefined,
            gameName: gameFilter || undefined,
          }),
          firebaseAnalyticsApi.getCurrentlyListeningUsers(appEnv),
        ]);

        firebaseAnalyticsApi.getDroppedListeners(appEnv).then((res) => {
          setDroppedListeners(res.success ? res.rows || [] : []);
        });

        if (dailyDetailsResponse.success) {
          setRows(dailyDetailsResponse.rows || []);
          const apiTotal = dailyDetailsResponse.total_rows ?? 0;
          setTotalRows(apiTotal);
          setHasNextPage(Boolean(dailyDetailsResponse.has_next_page));
          setHasPreviousPage(Boolean(dailyDetailsResponse.has_previous_page));
        } else {
          setError('Failed to fetch analytics data');
          setRows([]);
          setTotalRows(0);
          setHasNextPage(false);
          setHasPreviousPage(false);
        }

        if (listeningResponse.success) {
          setCurrentlyListeningUsers(listeningResponse.rows || []);
          setLiveUpdatedAtMs(Date.now());
          setLiveFetchFailed(false);
        } else {
          setCurrentlyListeningUsers([]);
          setLiveFetchFailed(true);
        }

        // Set stats from the stats API (first row contains aggregate data)
        if (statsResponse.success && statsResponse.rows && statsResponse.rows.length > 0) {
          setStats(statsResponse.rows[0]);
        } else {
          setStats(null);
          if (statsResponse.error) {
            setError(statsResponse.error);
          }
        }
      
      setExpandedRowKey(null);
      setExpandedEventDetails({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAll = async () => {
    if (!dateFrom || !dateTo) {
      setError('Please select both From and To dates.');
      return;
    }
    setExporting(true);
    try {
      // The backend caps `limit` at 1000 per request, so exporting the full current filter set
      // (not just the current 10-row page) means paging through in 1000-row chunks rather than
      // asking for everything in one call.
      const EXPORT_CHUNK_SIZE = 1000;
      const allRows: FirebaseUserDailyAudioRow[] = [];
      let offset = 0;
      for (;;) {
        const response = await firebaseAnalyticsApi.getDailyDetails({
          startDate: dateFrom,
          endDate: dateTo,
          app: appEnv,
          limit: EXPORT_CHUNK_SIZE,
          offset,
          userId: userIdFilter.trim() || undefined,
          gameName: gameNameFilter.trim() || undefined,
        });

        if (!response.success) {
          setError('Failed to export analytics data');
          return;
        }

        const page = response.rows || [];
        allRows.push(...page);
        offset += page.length;
        if (!response.has_next_page || page.length === 0) break;
      }

      exportToCsv(
        `user-audio-activity-${dateFrom}-to-${dateTo}.csv`,
        allRows,
        [
          { header: 'Date', value: (row) => row.date },
          { header: 'User ID', value: (row) => row.user_id ?? 'N/A' },
          { header: 'Game Name', value: (row) => row.game_name ?? '' },
          { header: 'Audio Played', value: (row) => row.audio_play_count ?? row.audio_played ?? 0 },
          { header: 'Audio Stopped', value: (row) => row.audio_stop_count ?? row.audio_stopped ?? 0 },
          { header: 'Total Events', value: (row) => (row.audio_play_count ?? row.audio_played ?? 0) + (row.audio_stop_count ?? row.audio_stopped ?? 0) },
          { header: 'Listening Duration (s)', value: (row) => row.listening_duration_seconds ?? 0 },
        ]
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while exporting data');
    } finally {
      setExporting(false);
    }
  };

  const fetchLiveListeningUsers = async () => {
    try {
      const [listeningResponse, droppedResponse, statsResponse] = await Promise.all([
        firebaseAnalyticsApi.getCurrentlyListeningUsers(appEnv),
        firebaseAnalyticsApi.getDroppedListeners(appEnv),
        firebaseAnalyticsApi.getStats({
          startDate: dateFrom,
          endDate: dateTo,
          app: appEnv,
          liveWindowMinutes: 1,
          userId: userIdFilter.trim() || undefined,
          gameName: gameNameFilter.trim() || undefined,
        }),
      ]);

      // Keep the previous list on a failed poll so cards don't flicker away.
      if (droppedResponse.success) {
        setDroppedListeners(droppedResponse.rows || []);
      }

      if (listeningResponse.success) {
        setCurrentlyListeningUsers(listeningResponse.rows || []);
        setLiveUpdatedAtMs(Date.now());
        setLiveFetchFailed(false);
      } else {
        setLiveFetchFailed(true);
      }
      if (statsResponse.success && statsResponse.rows && statsResponse.rows.length > 0) {
        setStats(statsResponse.rows[0]);
      }
    } catch (err) {
      console.error('Error polling live listening users:', err);
      setLiveFetchFailed(true);
    }
  };

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [dateFrom, dateTo, userIdFilter, gameNameFilter]);

  useEffect(() => {
    fetchMetrics();
  }, [dateFrom, dateTo, userIdFilter, gameNameFilter, currentPage]);

  // Poll live currently listening users and stats every 5 seconds
  useEffect(() => {
    if (livePaused) return undefined;
    const intervalId = window.setInterval(() => {
      fetchLiveListeningUsers();
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [appEnv, dateFrom, dateTo, userIdFilter, gameNameFilter, livePaused]);

  // Live ticker for currently listening users and expanded session details
  useEffect(() => {
    if (!expandedRowKey && !liveUpdatedAtMs && currentlyListeningUsers.length === 0 && droppedListeners.length === 0) return undefined;
    const id = window.setInterval(() => {
      setLiveClockSeconds(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [expandedRowKey, liveUpdatedAtMs, currentlyListeningUsers, droppedListeners]);

  // Helpers to apply cloud_function-filtered session data to counts/duration
  const getRowOverrides = (rowKey: string) => {
    const details = expandedEventDetails[rowKey];
    if (!details) return null;
    const sessionRecords = groupSessions(details);
    const listeningDurationSeconds = sessionRecords.reduce((acc, s) => acc + Math.max(0, s.durationSeconds), 0);
    const plays = sessionRecords.filter(s => s.playEvent).length;
    const stops = sessionRecords.filter(s => s.stopEvent).length;
    return {
      plays,
      stops,
      totalEvents: plays + stops,
      listeningDurationSeconds,
    };
  };

  const totals = useMemo(() => {
    return {
      plays: stats?.total_play_events ?? 0,
      stops: stats?.total_stop_events ?? 0,
      totalEvents: stats?.total_events ?? 0,
      activeUsers: currentlyListeningUsers.length > 0 ? currentlyListeningUsers.length : (stats?.active_users ?? 0),
      uniqueUsers: stats?.unique_users ?? 0,
      totalListeningDuration: stats?.listening_duration_seconds ?? 0,
    };
  }, [rows, stats, currentlyListeningUsers, expandedEventDetails]);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const paginatedRows = rows;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      setExpandedRowKey(null);
    }
  }, [currentPage, totalPages]);

  const buildRowDetails = (row: FirebaseUserDailyAudioRow) => {
    // Handle both naming conventions
    const plays = row.audio_play_count ?? row.audio_played ?? 0;
    const stops = row.audio_stop_count ?? row.audio_stopped ?? 0;
    
    const audioPlayEvents = Array.from({ length: Math.max(1, plays) }, (_, index) => ({
      user: row.user_id ?? 'unknown',
      stream_url: 'https://stream.example.com/live/game-audio',
      game_id: row.game_id ?? null,
      sport: 'football',
      latency_seconds: 2 + (index % 3),
      is_live: true,
      device_model: 'iPhone 15',
      os_version: 'iOS 18.1',
      app_version: '2.4.0',
    }));

    const audioStopEvents = Array.from({ length: Math.max(1, stops) }, (_, index) => ({
      user: row.user_id ?? 'unknown',
      reason: 'user action',
      position_seconds: Math.max(0, (index + 1) * 30),
      latency_seconds: 2 + (index % 3),
    }));

    const durationCount = Math.max(1, Math.max(plays, stops));
    const durationPerEvent = durationCount > 0 ? (row.play_duration_seconds ?? 0) / durationCount : 0;
    const listeningDurationEvents = Array.from({ length: durationCount }, (_, index) => ({
      user: row.user_id ?? 'unknown',
      duration_seconds: Math.max(0, Math.round(durationPerEvent + index)),
    }));

    return {
      audioPlayEvents,
      audioStopEvents,
      listeningDurationEvents,
    };
  };

  // Pair audio_play and audio_stop by ga_session_id & utc_time to produce session records with duration
  function groupSessions(details: UserGameEventDetail) {
    type SessionRecord = {
      sessionId?: string;
      utcTime?: string;
      playEvent?: AudioEvent;
      stopEvent?: AudioEvent;
      playTs: number | null;
      stopTs: number | null;
      durationSeconds: number;
      status: 'complete' | 'missing_stop' | 'missing_play' | 'invalid_time';
    };

    const getStringParam = (event: AudioEvent | ListeningDurationEvent, key: string): string | undefined => {
      const param = event.event_params?.find(p => p.key === key);
      const value = param?.value;
      if (!value) return undefined;
      if (value.string_value !== null && value.string_value !== undefined) return value.string_value;
      if (value.int_value !== null && value.int_value !== undefined) return String(value.int_value);
      if (value.double_value !== null && value.double_value !== undefined) return String(value.double_value);
      if (value.float_value !== null && value.float_value !== undefined) return String(value.float_value);
      return undefined;
    };

    const parseTimestampSeconds = (event: AudioEvent | ListeningDurationEvent, fallbackMs: number): number | null => {
      const eventTimeParam = event.event_params?.find(p => p.key === 'event_time');
      const raw =
        (eventTimeParam?.value?.int_value ?? eventTimeParam?.value?.double_value) ??
        (eventTimeParam?.value?.string_value ? Number(eventTimeParam.value.string_value) : undefined);
      if (raw && !Number.isNaN(raw)) {
        const val = Number(raw);
        if (val > 1e14) {
          return Math.floor(val / 1_000_000); // microseconds -> seconds
        }
        if (val > 1e11) {
          return Math.floor(val / 1_000); // milliseconds -> seconds
        }
        return Math.floor(val); // already seconds
      }
      return Number.isFinite(fallbackMs) ? Math.floor(fallbackMs / 1000) : null;
    };

    const sessions = new Map<string, SessionRecord>();

    const getSessionRecord = (sessionId?: string) => {
      const key = sessionId || 'no-session';
      if (!sessions.has(key)) {
        sessions.set(key, {
          sessionId,
          utcTime: undefined,
          playEvent: undefined,
          stopEvent: undefined,
          playTs: null,
          stopTs: null,
          durationSeconds: 0,
          status: 'missing_stop',
        });
      }
      return sessions.get(key)!;
    };

    // Only keep first play per session
    details.audio_play_events?.forEach(event => {
      const sessionId = getStringParam(event, 'ga_session_id');
      const rec = getSessionRecord(sessionId);
      if (!rec.playEvent) {
        rec.playEvent = event;
        rec.playTs = parseTimestampSeconds(event, event.event_timestamp);
      }
      sessions.set(sessionId || 'no-session', rec);
    });

    // Only keep first stop per session
    details.audio_stop_events?.forEach(event => {
      const sessionId = getStringParam(event, 'ga_session_id');
      const rec = getSessionRecord(sessionId);
      if (!rec.stopEvent) {
        rec.stopEvent = event;
        rec.stopTs = parseTimestampSeconds(event, event.event_timestamp);
      }

      if (rec.playTs !== null && rec.stopTs !== null) {
        const diff = rec.stopTs - rec.playTs;
        rec.durationSeconds = diff > 0 ? diff : 0;
        rec.status = diff >= 0 ? 'complete' : 'invalid_time';
      } else {
        rec.durationSeconds = 0;
        rec.status = rec.playEvent ? 'missing_stop' : 'missing_play';
      }
      sessions.set(sessionId || 'no-session', rec);
    });

    return Array.from(sessions.values());
  }

  const handleShowDetails = async (row: FirebaseUserDailyAudioRow, rowKey: string) => {
    if (expandedRowKey === rowKey) {
      // Collapse
      setExpandedRowKey(null);
    } else {
      // Expand and fetch detailed events
      if (!row.user_id || !row.date) {
        setError('Cannot fetch details: missing user_id or date');
        return;
      }

      // Check if we already have this data cached
      if (expandedEventDetails[rowKey]) {
        setExpandedRowKey(rowKey);
      } else {
        try {
          setRowLoadingKey(rowKey);
          const gameId = row.game_id ?? null;
          const details = await firebaseAnalyticsApi.getUserGameEventDetails({
            date: row.date,
            userId: row.user_id,
            gameId: gameId,
            app: appEnv,
          });  

          setExpandedEventDetails(prev => ({
            ...prev,
            [rowKey]: details,
          }));
          setExpandedRowKey(rowKey);
        } catch (err) {
          setError(`Failed to fetch event details: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
        finally {
          setRowLoadingKey(null);
        }
      }
    }
  };

  // One currently-listening card.
  const renderListeningCard = (user: CurrentlyListeningUserRow, idx: number) => {
    const startTsMs = user.event_time_ms ?? (user.event_timestamp ? user.event_timestamp / 1000 : null);
    const elapsedSec = startTsMs ? Math.max(0, liveClockSeconds - Math.floor(startTsMs / 1000)) : 0;
    const initials = getInitials(user.user_id || '');

    return (
      <div
        key={(user.user_id || 'user') + '-' + idx}
        style={{
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '16px',
          padding: '14px 16px',
          background: 'var(--card-bg-subtle, #f8fafc)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              background: '#dcfce7',
              color: '#15803d',
              fontWeight: 700,
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              letterSpacing: '0.5px',
            }}
          >
            {initials}
          </div>
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              wordBreak: 'break-all',
              color: 'var(--text-primary, #0f172a)',
              fontFamily: 'monospace, sans-serif',
            }}
          >
            {user.user_id || 'Unknown User'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <LocationTag location={userLocation(user)} />
            <AttributionBadge attribution={userAttribution(user)} />
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          <span className="analytics-live-chip" style={{ fontSize: '11px', padding: '3px 8px' }}>
            <span className="live-eq" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            {user.status ? user.status.toUpperCase() : 'LISTENING'}
          </span>

          {user.device_type && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: '12px',
                background: 'rgba(147, 51, 234, 0.1)',
                color: '#7e22ce',
                textTransform: 'uppercase',
              }}
            >
              📱 {user.device_type}
            </span>
          )}

          {user.app_type && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: '12px',
                background: 'rgba(234, 179, 8, 0.1)',
                color: '#a16207',
              }}
            >
              {user.app_type}
            </span>
          )}

          {(user.game_name || user.game_id) && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 500,
                padding: '3px 9px',
                borderRadius: '12px',
                background: 'var(--badge-bg, #e2e8f0)',
                color: 'var(--text-secondary, #475569)',
                maxWidth: '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={user.game_name || user.game_id || ''}
            >
              {user.game_name || user.game_id}
            </span>
          )}
        </div>


      </div>
    );
  };

  // One dropped-listener card (same layout as the Currently Listening cards).
  const renderDroppedCard = (user: CurrentlyListeningUserRow, idx: number) => {
    const droppedAtMs = user.event_time_ms ?? (user.event_timestamp ? user.event_timestamp / 1000 : null);
    const agoSec = droppedAtMs ? Math.max(0, liveClockSeconds - Math.floor(droppedAtMs / 1000)) : null;
    // Ended sessions are shown as a drop, a sign-up (identity_changed) or a purchase
    // (converted-*), each with its own colour; sign-ups/purchases also show the account email.
    const outcome = sessionOutcome(user);
    const product = PURCHASE_EVENTS[normEvent(user)];
    const tone = outcome === 'signup'
      ? { accent: '#15803d', border: '1px solid #16a34a', bg: 'rgba(34, 197, 94, 0.06)', chipBg: 'rgba(34, 197, 94, 0.12)', avatarBg: '#dcfce7', chip: 'Signed up', verb: 'Signed up', emailLabel: 'Signed up as' }
      : outcome === 'purchase' && product
        ? { accent: product.color, border: `1px solid ${product.color}`, bg: product.bg.replace(/0\.1\)$/, '0.05)'), chipBg: product.bg, avatarBg: product.bg, chip: product.label, verb: 'Purchased', emailLabel: 'Purchased by' }
        : { accent: '#b91c1c', border: '1px solid var(--border-color, #e2e8f0)', bg: 'var(--card-bg-subtle, #f8fafc)', chipBg: 'rgba(239, 68, 68, 0.1)', avatarBg: '#fee2e2', chip: user.status || 'Stopped', verb: 'Dropped', emailLabel: '' };
    const converted = outcome !== 'drop';
    // Dropped because the user signed in/up: highlight the email they switched to.
    const switchLabel = outcome === 'drop' ? IDENTITY_SWITCH_REASONS[normReason(user)] : undefined;
    const switchedEmail = user.email || (isSignedInUser(user) ? user.user_id : null);

    return (
      <div
        key={(user.user_id || 'user') + '-' + idx}
        style={{
          border: tone.border,
          borderRadius: '16px',
          padding: '14px 16px',
          background: tone.bg,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              background: tone.avatarBg,
              color: tone.accent,
              fontWeight: 700,
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              letterSpacing: '0.5px',
            }}
          >
            {getInitials(user.user_id || '')}
          </div>
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              wordBreak: 'break-all',
              color: 'var(--text-primary, #0f172a)',
              fontFamily: 'monospace, sans-serif',
            }}
          >
            {user.user_id || 'Unknown User'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <LocationTag location={userLocation(user)} />
            <AttributionBadge attribution={userAttribution(user)} />
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '12px', background: tone.chipBg, color: tone.accent, textTransform: 'uppercase' }}>
            {tone.chip}
          </span>

          {user.device_type && (
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '12px', background: 'rgba(147, 51, 234, 0.1)', color: '#7e22ce', textTransform: 'uppercase' }}>
              📱 {user.device_type}
            </span>
          )}

          {user.app_type && (
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '12px', background: 'rgba(234, 179, 8, 0.1)', color: '#a16207' }}>
              {user.app_type}
            </span>
          )}

          {(user.game_name || user.game_id) && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 500,
                padding: '3px 9px',
                borderRadius: '12px',
                background: 'var(--badge-bg, #e2e8f0)',
                color: 'var(--text-secondary, #475569)',
                maxWidth: '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={user.game_name || user.game_id || ''}
            >
              {user.game_name || user.game_id}
            </span>
          )}
        </div>

        {switchLabel && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
              padding: '8px 10px', borderRadius: '10px',
              background: 'rgba(37, 99, 235, 0.08)', border: '1px solid rgba(37, 99, 235, 0.35)',
              fontSize: '12px', color: '#1e3a8a',
            }}
          >
            <UserCheck size={14} />
            <span style={{ fontWeight: 600 }}>{switchLabel}:</span>
            <strong style={{ fontFamily: 'monospace, sans-serif', fontSize: '13px', color: '#1d4ed8', wordBreak: 'break-all' }}>
              {switchedEmail || 'email not available'}
            </strong>
          </div>
        )}

        {converted && (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary, #475569)', wordBreak: 'break-all' }}>
            {tone.emailLabel}:{' '}
            <strong style={{ color: tone.accent, fontFamily: 'monospace, sans-serif' }}>{user.email || (isSignedInUser(user) ? user.user_id : 'email not available')}</strong>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '12px' }}>
          <span style={{ color: 'var(--text-secondary, #475569)' }}>
            {outcome === 'purchase' ? 'Event' : 'Reason'}:{' '}
            <strong style={{ color: tone.accent, fontFamily: 'monospace, sans-serif' }}>{outcome === 'purchase' ? user.event : (user.reason || 'unknown')}</strong>
          </span>
          <span style={{ color: 'var(--text-secondary, #64748b)' }} title={droppedAtMs ? new Date(droppedAtMs).toLocaleString() : ''}>
            {tone.verb} {formatClockTime(droppedAtMs)}{agoSec !== null ? ` · ${formatDuration(agoSec)} ago` : ''}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <Activity size={24} />
        User Audio Analytics
      </h2>

      {/* Single page, two sub-sections: live first, then history. */}
      <section className="analytics-subsection" aria-labelledby="analytics-realtime-heading">
        <h3 id="analytics-realtime-heading" className="analytics-subsection-title">
          <span className="analytics-subsection-pill">
            <span className="analytics-live-dot" aria-hidden="true" />
            Real Time Logs
          </span>
        </h3>
        <div className="teams-editor-tabs" role="tablist" aria-label="Real time log views">
          {([
            { key: 'live', label: 'Live Sessions' },
            { key: 'free-trial-past', label: 'Free Trial Past Logs' },
          ] as const).map((view) => (
            <button
              key={view.key}
              type="button"
              role="tab"
              aria-selected={rtView === view.key}
              className={`teams-editor-tab ${rtView === view.key ? 'active' : ''}`}
              onClick={() => setRtView(view.key)}
            >
              {view.label}
            </button>
          ))}
        </div>
        {rtView === 'free-trial-past' && <FreeTrialPastLogs appEnv={appEnv} todayValue={todayValue} />}
      {/* Live view stays mounted (only hidden) so its 5s poll and state survive tab switches. */}
      <div style={{ display: rtView === 'live' ? 'block' : 'none' }}>
      {/* All real-time blocks live in one card; inner cards are flattened via .nested-cards. */}
      <Card
        title="Today's Live Sessions"
        description="Live listeners, drops, sign-ups and purchases from today's sessions, refreshed every 5 seconds"
        headerAction={
          /* ===== Real Time Logs: freshness + pause ===== */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', fontSize: '12px' }}>
            {liveFetchFailed ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600, padding: '4px 10px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' }}>
                <AlertTriangle size={13} />
                Live data unavailable{liveUpdatedAtMs ? ` · last good update ${formatAgo(liveClockSeconds - Math.floor(liveUpdatedAtMs / 1000))} ago` : ''}
              </span>
            ) : (
              <span style={{ color: 'var(--text-secondary, #64748b)' }}>
                {livePaused ? 'Paused · ' : ''}
                {liveUpdatedAtMs ? `Updated ${formatAgo(liveClockSeconds - Math.floor(liveUpdatedAtMs / 1000))} ago` : 'Loading…'}
              </span>
            )}
            <button type="button" className="btn btn-small btn-outline" onClick={() => setLivePaused((paused) => !paused)}>
              {livePaused ? <Play size={14} /> : <Pause size={14} />}
              {livePaused ? 'Resume' : 'Pause'}
            </button>
          </div>
        }
      >
      <div className="nested-cards">

      {/* ===== Real Time Logs: filter bar ===== */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div className="analytics-filter">
          <span className="analytics-filter-label">User / Email</span>
          <input
            className="form-input analytics-filter-input"
            type="text"
            placeholder="Search user ID or email"
            value={rtSearch}
            onChange={(e) => setRtSearch(e.target.value)}
          />
        </div>
        <div className="analytics-filter">
          <span className="analytics-filter-label">Platform</span>
          <select className="form-input analytics-filter-input" value={rtPlatform} onChange={(e) => setRtPlatform(e.target.value as PlatformFilter)}>
            <option value="all">All</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>
        </div>
        <div className="analytics-filter">
          <span className="analytics-filter-label">Plan</span>
          <select className="form-input analytics-filter-input" value={rtPlan} onChange={(e) => setRtPlan(e.target.value as PlanFilter)}>
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="trial">Free trial</option>
          </select>
        </div>
        {rtFiltersActive && (
          <button type="button" className="btn btn-small btn-outline" onClick={() => { setRtSearch(''); setRtPlatform('all'); setRtPlan('all'); }}>
            Clear
          </button>
        )}
      </div>

      {/* ===== Real Time Logs: KPI strip ===== */}
      <div className="analytics-summary" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {[
          { label: 'Live Now', value: rtKpis.live, meta: 'Streaming right now', icon: <Radio size={20} />, color: '#16a34a' },
          { label: 'Paid', value: rtKpis.paid, meta: 'Live, not on free trial', icon: <UsersRound size={20} />, color: undefined },
          { label: 'Free Trial', value: rtKpis.trial, meta: 'Live on free trial', icon: <Timer size={20} />, color: '#b45309' },
          { label: 'Dropped Today', value: rtKpis.dropped, meta: 'Ended, excluding sign-ups and purchases', icon: <Square size={16} />, color: '#dc2626' },
          { label: 'Signed Up Today', value: rtKpis.signedUp, meta: 'Ended with identity_changed', icon: <UserCheck size={20} />, color: '#15803d' },
          { label: 'Purchased Today', value: rtKpis.purchased, meta: allPurchasedByType.map((t) => `${t.label} ${t.count}`).join(' · '), icon: <Receipt size={20} />, color: '#7e22ce' },
        ].map((kpi) => (
          <div key={kpi.label} className="analytics-summary-card">
            <div className="analytics-summary-card-head">
              <div className="analytics-summary-label">{kpi.label}</div>
              <div className="analytics-summary-icon">{kpi.icon}</div>
            </div>
            <div className="analytics-summary-value" style={kpi.color ? { color: kpi.color } : undefined}>{kpi.value.toLocaleString()}</div>
            <div className="analytics-summary-meta">{kpi.meta}</div>
          </div>
        ))}
      </div>

      {/* ===== Real Time Logs: per-game breakdown + drop reasons ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '16px', marginBottom: '16px', alignItems: 'start' }}>
        <Card
          title="Per-Game Breakdown"
          description="Live listeners and today's drops, sign-ups and purchases per game, busiest first"
          collapsible
          open={perGameOpen}
          onToggle={() => setPerGameOpen((open) => !open)}
          headerAction={<span className="card-count-chip">{rtGameBreakdown.length} {rtGameBreakdown.length === 1 ? 'game' : 'games'}</span>}
        >
          {rtGameBreakdown.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '16px 0', textAlign: 'center' }}>No games with activity today.</div>
          ) : (
            /* One compact card per game with colour-coded metric tiles (same layout as the
               Paywall Counter's per-user cards). */
            <div style={METRIC_CARD_GRID}>
              {rtGameBreakdown.map((row) => {
                const total = row.live + row.dropped + row.signedUp;
                return (
                  <MetricCard
                    key={row.game}
                    title={row.game}
                    meta={`${total} session${total === 1 ? '' : 's'} today`}
                    badge={row.live > 0 ? (
                      <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 700, color: '#15803d' }}>
                        <span className="analytics-live-dot" aria-hidden="true" />
                        LIVE
                      </span>
                    ) : undefined}
                    metrics={[
                      { label: 'Live', value: row.live, color: '#16a34a' },
                      { label: 'Paid', value: row.paid, color: '#233F8C' },
                      { label: 'Trial', value: row.trial, color: '#b45309' },
                      { label: 'Dropped', value: row.dropped, color: '#dc2626' },
                      { label: 'Signed up', value: row.signedUp, color: '#15803d' },
                      { label: 'Purchased', value: row.purchased, color: '#7e22ce' },
                    ]}
                  />
                );
              })}
            </div>
          )}
        </Card>

        <Card
          title="Drop Reasons"
          description="Why today's sessions dropped (sign-ups and purchases excluded). A spike in app kills or disconnects during a game usually means a stream or app issue"
          collapsible
          open={dropReasonsOpen}
          onToggle={() => setDropReasonsOpen((open) => !open)}
          headerAction={<span className="card-count-chip">{allDroppedOnlyUsers.length} {allDroppedOnlyUsers.length === 1 ? 'drop' : 'drops'}</span>}
        >
          {rtDropReasons.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '16px 0', textAlign: 'center' }}>No drops today.</div>
          ) : (
            /* One small card per reason (busiest first): the reason and its drop count. */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', gap: '10px' }}>
              {rtDropReasons.map((row) => (
                <MetricCard
                  key={row.reason}
                  title={row.reason}
                  titleColor="#b91c1c"
                  metrics={[{ label: 'Drops', value: row.count, color: '#dc2626' }]}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ===== Real Time Logs: game tabs - the user cards below are scoped to the selected game ===== */}
      <div className="teams-editor-tabs rt-game-tabs" role="tablist" aria-label="Today's games">
        {[...rtGameTabs, { game: ALL_GAMES_TAB, live: filteredLiveUsers.length, ended: filteredDroppedUsers.length }].map((tab) => {
          const isAll = tab.game === ALL_GAMES_TAB;
          const active = activeGameTab === tab.game;
          return (
            <button
              key={tab.game}
              type="button"
              role="tab"
              aria-selected={active}
              className={`teams-editor-tab ${active ? 'active' : ''}`}
              onClick={() => setRtGameTab(tab.game)}
              title={isAll ? 'All of today\'s games' : tab.game}
              style={{ maxWidth: '300px' }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isAll ? 'All games' : tab.game}</span>
              <span className={`rt-game-tab-count${tab.live > 0 ? ' live' : ''}`}>
                {tab.live > 0 && <span className="analytics-live-dot" aria-hidden="true" />}
                {tab.live} live
              </span>
            </button>
          );
        })}
      </div>
      {rtGameTabs.length === 0 && (
        <div className="analytics-summary-meta" style={{ padding: '4px 0 12px' }}>
          No games scheduled or active today.
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <Card
          title="Paid Listeners"
          {...userCardToggle('paid')}
          description="Paid users currently streaming audio (free trial users are listed separately below)"
          headerAction={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="analytics-live-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '4px 10px', borderRadius: '12px', background: 'rgba(34, 197, 94, 0.1)', color: '#16a34a' }}>
                <span className="analytics-live-dot" />
                {paidListeners.length} LIVE
              </span>
            </div>
          }
        >
          {paidListeners.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '16px 0', textAlign: 'center' }}>
              No users currently listening.
            </div>
          ) : (
            <GameGroupedCards showTabs={activeGameTab === ALL_GAMES_TAB}
              users={paidListeners}
              renderCard={renderListeningCard}
              accent="#16a34a"
              accentBg="rgba(34, 197, 94, 0.1)"
              countLabel="listening"
            />
          )}
        </Card>
      </div>

      <div style={{ marginTop: '16px', position: 'relative' }}>
        <Card
          title="Free Trial Listeners"
          {...userCardToggle('trial')}
          description="Live listeners with isFreeTrial: true - Anonymous (user is a UUID) and Signed-in (user is an email)"
          headerAction={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '4px 10px', borderRadius: '12px', background: 'rgba(245, 158, 11, 0.12)', color: '#b45309' }}>
                <Timer size={12} />
                {freeTrialListeners.length} USERS
              </span>
            </div>
          }
        >
          {freeTrialListeners.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '16px 0', textAlign: 'center' }}>
              No free trial users currently listening.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              <section aria-label="Anonymous users">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                  <UsersRound size={16} /> Anonymous Users
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#b45309' }}>{anonymousTrialUsers.length}</span>
                </div>
                {anonymousTrialUsers.length === 0 ? (
                  <div className="analytics-summary-meta" style={{ padding: '8px 0' }}>No anonymous users currently listening.</div>
                ) : (
                  <GameGroupedCards showTabs={activeGameTab === ALL_GAMES_TAB} users={anonymousTrialUsers} renderCard={renderListeningCard} accent="#b45309" accentBg="rgba(245, 158, 11, 0.12)" countLabel="anonymous" />
                )}
              </section>
              <section aria-label="Signed-in users">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                  <UserCheck size={16} /> Signed-in Users
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#15803d' }}>{signedInTrialUsers.length}</span>
                </div>
                {signedInTrialUsers.length === 0 ? (
                  <div className="analytics-summary-meta" style={{ padding: '8px 0' }}>No signed-in free trial users currently listening.</div>
                ) : (
                  <GameGroupedCards showTabs={activeGameTab === ALL_GAMES_TAB} users={signedInTrialUsers} renderCard={renderListeningCard} accent="#15803d" accentBg="rgba(34, 197, 94, 0.12)" countLabel="signed in" />
                )}
              </section>
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginTop: '16px', position: 'relative' }}>
        <Card
          title="Dropped Listeners"
          {...userCardToggle('dropped')}
          description="Users who are no longer live today (latest session is not playing), with the reason"
          headerAction={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '4px 10px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' }}>
                <Square size={10} fill="currentColor" />
                {droppedOnlyUsers.length} DROPPED
              </span>
            </div>
          }
        >
          {droppedOnlyUsers.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '16px 0', textAlign: 'center' }}>
              No dropped listeners today.
            </div>
          ) : (
            <GameGroupedCards showTabs={activeGameTab === ALL_GAMES_TAB}
              users={droppedOnlyUsers}
              renderCard={renderDroppedCard}
              accent="#dc2626"
              accentBg="rgba(239, 68, 68, 0.1)"
              countLabel="dropped"
            />
          )}
        </Card>
      </div>

      <div style={{ marginTop: '16px', position: 'relative' }}>
        <Card
          title="Today's Purchased Users"
          {...userCardToggle('purchased')}
          description="Today's sessions (live or ended) with a purchase event (converted_single_game / _football_season / _basketball_season / _all_sport) and isFreeTrial: false"
          headerAction={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '4px 10px', borderRadius: '12px', background: 'rgba(147, 51, 234, 0.1)', color: '#7e22ce' }}>
                <Receipt size={12} />
                {purchasedUsers.length} PURCHASED
              </span>
            </div>
          }
        >
          {/* Per-product counts (always shown, zeros included, so all four types are visible). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
            {purchasedByType.map((type) => (
              <span
                key={type.reason}
                title={type.reason}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, padding: '6px 12px', borderRadius: '999px', background: type.bg, color: type.color }}
              >
                {type.label}
                <strong style={{ fontSize: '14px' }}>{type.count}</strong>
              </span>
            ))}
          </div>
          {purchasedUsers.length === 0 ? (
            <div className="analytics-summary-meta" style={{ padding: '16px 0', textAlign: 'center' }}>
              No purchases today.
            </div>
          ) : (
            <GameGroupedCards showTabs={activeGameTab === ALL_GAMES_TAB}
              users={purchasedUsers}
              renderCard={renderDroppedCard}
              accent="#7e22ce"
              accentBg="rgba(147, 51, 234, 0.1)"
              countLabel="purchased"
            />
          )}
        </Card>
      </div>
      </div>
      </Card>
      </div>
      </section>

      <section className="analytics-subsection" aria-labelledby="analytics-historic-heading">
        <h3 id="analytics-historic-heading" className="analytics-subsection-title">
          <span className="analytics-subsection-pill">
            <Calendar size={13} />
            Historic Logs
          </span>
        </h3>
      <div>
      <div style={{ position: 'relative' }}>
      <Card
        title="Daily User Audio Activity"
        description="Per-user daily view of audio play, stop, and playback duration"
        headerAction={
          <div className="analytics-filters">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <ThemedDatePicker label="From" value={dateFrom} max={todayValue} onChange={setDateFrom} />
              <ThemedDatePicker label="To" value={dateTo} max={todayValue} onChange={setDateTo} />
            </div>

            <div style={{ flexBasis: '100%', height: 0 }} />

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="analytics-filter">
                <span className="analytics-filter-label">User ID</span>
                <input
                  className="form-input analytics-filter-input"
                  type="text"
                  placeholder="Filter by user ID"
                  value={userIdFilter}
                  onChange={(e) => setUserIdFilter(e.target.value)}
                />
              </div>

              <div className="analytics-filter">
                <span className="analytics-filter-label">Game Name</span>
                <input
                  className="form-input analytics-filter-input"
                  type="text"
                  placeholder="Filter by game name"
                  value={gameNameFilter}
                  onChange={(e) => setGameNameFilter(e.target.value)}
                />
              </div>
          
              {/* <button
                type="button"
                className="btn btn-outline btn-small"
                onClick={fetchMetrics}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Filter'}
              </button> */}
              <button
                type="button"
                className="btn btn-small btn-primary"
                onClick={fetchMetrics}
                disabled={loading}
                aria-label="Refresh analytics"
              >
                <RefreshCw size={15} className={loading ? 'animate-spin spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
        }
      >

        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10
          }}>
            <Loader2 size={32} className="animate-spin spin" />
          </div>
        )}
        <div className="analytics-summary">
          <div className="analytics-summary-card">
            <div className="analytics-summary-card-head">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <div className="analytics-summary-label">Active Users</div>
                {totals.activeUsers > 0 && (
                  <div className="analytics-live-indicator">
                    <span className="analytics-live-dot" />
                    LIVE
                  </div>
                )}
              </div>
              <div className="analytics-summary-icon">
                <Radio size={22} />
              </div>
            </div>
            <div className="analytics-summary-value">{totals.activeUsers.toLocaleString()}</div>
            <div className="analytics-summary-meta">Currently live users</div>
          </div>
          <div className="analytics-summary-card">
            <div className="analytics-summary-card-head">
              <div className="analytics-summary-label">Total Listening Duration</div>
              <div className="analytics-summary-icon">
                <Timer size={22} />
              </div>
            </div>
            <div className="analytics-summary-value">{formatDuration(totals.totalListeningDuration)}</div>
            <div className="analytics-summary-meta">Across selected range</div>
          </div>
          <div className="analytics-summary-card">
            <div className="analytics-summary-card-head">
              <div className="analytics-summary-label">Play and Stop Events</div>
              <div className="analytics-summary-icon">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Play size={14} />
                  <Square size={12} />
                </div>
              </div>
            </div>
            <div className="analytics-summary-value">
              {totals.plays.toLocaleString()} / {totals.stops.toLocaleString()}
            </div>
            <div className="analytics-summary-meta">Play / Stop across selected range</div>
          </div>
          <div className="analytics-summary-card">
            <div className="analytics-summary-card-head">
              <div className="analytics-summary-label">Total Unique Users</div>
              <div className="analytics-summary-icon">
                <UsersRound size={22} />
              </div>
            </div>
            <div className="analytics-summary-value">{totals.uniqueUsers.toLocaleString()}</div>
            <div className="analytics-summary-meta">Unique active users in selected date range</div>
          </div>
        </div>
      </Card>
      </div>
      </div>

      <div style={{ marginTop: '16px', position: 'relative' }}>
        <Card
          title="Event Data By Date"
          headerAction={
            <Button variant="outline" onClick={handleExportAll} loading={exporting} disabled={totalRows === 0}>
              <Download size={14} />
              Export All
            </Button>
          }
        >
          {loading && (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
              borderRadius: '14px',
            }}>
              <Loader2 size={28} className="animate-spin spin" />
            </div>
          )}
          {error && (
            <div className="response-container" style={{ marginBottom: '16px' }}>
              <div className="response-label">Error</div>
              <div className="response-content response-error">{error}</div>
            </div>
          )}

          {rows.length === 0 && !loading ? (
            <div className="analytics-summary-meta">No user audio data found for the selected filters.</div>
          ) : (
            <div>
            <div className="table-scroll-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Details</th>
                    <th>Date</th>
                    <th>User ID</th>
                    <th>Game Name </th>
                    {/* <th></th> */}
                    <th>Audio Played</th>
                    <th>Audio Stopped</th>
                    <th>Total Events</th>
                    <th>Listening Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 20 }}>
                    No data found
                  </td>
                </tr>
                  ) : (
                  paginatedRows.map((row, index) => {
                  const rowKey = `${row.date}-${row.user_id ?? 'unknown'}-${index}`;
                  const isExpanded = expandedRowKey === rowKey;
                    const overrides = getRowOverrides(rowKey);
                    const plays = overrides?.plays ?? row.audio_play_count ?? row.audio_played ?? 0;
                    const stops = overrides?.stops ?? row.audio_stop_count ?? row.audio_stopped ?? 0;
                    const listeningDurationSeconds = overrides?.listeningDurationSeconds ?? row.listening_duration_seconds ?? 0;

                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                  <td>
                    <button
                      type="button"
                      className={`analytics-row-toggle ${isExpanded ? 'expanded' : ''}`}
                      disabled={rowLoadingKey === rowKey}
                      onClick={() => handleShowDetails(row, rowKey)}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Hide event details' : 'Show event details'}
                    >
                      {rowLoadingKey === rowKey ? (
                        <Loader2 size={14} className="animate-spin spin" />
                      ) : (
                        <ChevronDown size={14} className="analytics-row-toggle-chevron" />
                      )}
                      {isExpanded ? 'Hide' : 'Show'}
                    </button>
                  </td>
                  <td>{row.date}</td>
                  <td className="analytics-user-cell">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span>{row.user_id ?? 'N/A'}</span>
                      <AttributionBadge attribution={attributionFor(row.user_id)} size={20} />
                    </span>
                    {/* {row.is_live && (
                      <span className="analytics-live-chip">
                        <span className="live-eq" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </span>
                        LISTENING
                      </span>
                    )} */}
                  </td>
                  <td className='tableViewGame'>
                    <span>{row.game_name ?? '-'}</span>
                    {/* <span>{row.game_id ?? '-'}</span> */}
                  </td>
                          <td>{plays.toLocaleString()}</td>
                          <td>{stops.toLocaleString()}</td>
                          <td>{(plays + stops).toLocaleString()}</td>
                          <td>{formatDuration(listeningDurationSeconds)}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8}>
                              <div className="response-container analytics-event-details-container" style={{ marginTop: '8px' }}>
                                <div className="response-label analytics-event-details-label">Event Details</div>
                                {expandedEventDetails[rowKey] ? (
                                  (() => {
                                    const apiDetails = expandedEventDetails[rowKey];
                                    const sessionRecords = groupSessions(apiDetails);
                                    return (
                                      <div className="session-table-wrapper">
                                        {sessionRecords.length > 0 ? (
                                          <table className="session-table">
                                            <colgroup>
                                              <col style={{ width: '8%' }} />
                                              <col style={{ width: '24%' }} />
                                              <col style={{ width: '24%' }} />
                                              <col style={{ width: '24%' }} />
                                              <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <thead>
                                              <tr>
                                                <th>#</th>
                                                <th>Session ID</th>
                                                <th>Play (UTC)</th>
                                                <th>Stop (UTC)</th>
                                                <th>Listening Duration</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {sessionRecords.map((session, idx) => {
                                                const isLive = session.status === 'missing_stop' && session.playTs !== null;
                                                const displayDurationSeconds = isLive
                                                  ? Math.max(0, liveClockSeconds - (session.playTs ?? liveClockSeconds))
                                                  : session.durationSeconds;

                                                return (
                                                  <tr key={`session-${idx}`}>
                                                    <td>{idx + 1}</td>
                                                    <td>{session.sessionId ?? 'N/A'}</td>
                                                    <td>{session.playTs ? new Date(session.playTs * 1000).toUTCString() : 'missing'}</td>
                                                    <td>
                                                      {session.stopTs ? (
                                                        new Date(session.stopTs * 1000).toUTCString()
                                                      ) : (
                                                        <span className="listening-indicator">
                                                          <span className="listening-dot" aria-hidden="true" />
                                                          Listening…
                                                        </span>
                                                      )}
                                                    </td>
                                                    <td>
                                                      <span className={`session-chip ${isLive ? 'session-chip-live' : ''}`}>
                                                        {formatDuration(displayDurationSeconds)}
                                                        {isLive ? ' · Live' : ''}
                                                      </span>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        ) : (
                                          <div className="analytics-summary-meta">No sessions</div>
                                        )}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  (() => {
                                    const details = buildRowDetails(row);
                                    const syntheticSessions = details.audioPlayEvents.map((play, idx) => {
                                      const stop = details.audioStopEvents[idx];
                                      const duration = details.listeningDurationEvents[idx]?.duration_seconds ?? 0;
                                      return {
                                        sessionId: `synthetic-${idx + 1}`,
                                        utcTime: undefined as string | undefined,
                                        playTs: null as number | null,
                                        stopTs: null as number | null,
                                        durationSeconds: duration,
                                        status: stop ? 'complete' as const : 'missing_stop' as const,
                                        play,
                                        stop,
                                      };
                                    });
                                    return (
                                      <div className="session-table-wrapper">
                                        {syntheticSessions.length > 0 ? (
                                          <table className="session-table">
                                            <colgroup>
                                              <col style={{ width: '8%' }} />
                                              <col style={{ width: '24%' }} />
                                              <col style={{ width: '24%' }} />
                                              <col style={{ width: '24%' }} />
                                              <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <thead>
                                              <tr>
                                                <th>#</th>
                                                <th>Session ID</th>
                                                <th>Play (UTC)</th>
                                                <th>Stop (UTC)</th>
                                                <th>Listening Duration</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {syntheticSessions.map((session, idx) => (
                                                <tr key={`session-syn-${idx}`}>
                                                  <td>{idx + 1}</td>
                                                  <td>{session.sessionId}</td>
                                                  <td>placeholder</td>
                                                  <td>
                                                    <span className="listening-indicator">
                                                      <span className="listening-dot" aria-hidden="true" />
                                                      Listening…
                                                    </span>
                                                  </td>
                                                  <td><span className="session-chip">{formatDuration(session.durationSeconds)}</span></td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        ) : (
                                          <div className="analytics-summary-meta">No sessions</div>
                                        )}
                                      </div>
                                    );
                                  })()
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }))}
                </tbody>
              </table>
            </div>
            <div className="analytics-pagination">
              <div className="analytics-summary-meta">
                Page {currentPage} of {totalPages} · Showing {(totalRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1)}-
                {Math.min(currentPage * PAGE_SIZE, totalRows)} of {totalRows}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="btn btn-outline btn-small"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={loading || !hasPreviousPage}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-small"
                  onClick={() => setCurrentPage((page) => page + 1)}
                  disabled={loading || !hasNextPage}
                >
                  Next
                </button>
              </div>
            </div>
            </div>
          )}
        </Card>
      </div>
      </section>
    </div>
  );
}
